import { useEffect, useRef } from "react"
import { useDispatch, useSelector } from "react-redux"
import { ThunkDispatch } from "@reduxjs/toolkit"
import { Modal } from "flowbite-react"
import { Slider } from 'primereact/slider'
import { itemRemovalActions } from '../../reducers/itemRemoval'
import {
  CircleOutlineIcon,
  RedoIcon,
  SquareOutlineIcon,
  TriangleOutlineIcon,
  UndoIcon
} from '../SVGIcons'
import FurnitureRemovalDrawing from './FurnitureRemovalDrawing'

type BrushShape = "circle" | "triangle" | "square"

/**
 * 家具部分消去の描画ポップアップ
 * AIEraserと同じフルスクリーン・2行ツールバー構造
 */
const FurnitureRemovalDrawingPopup = () => {
  const dispatch = useDispatch<ThunkDispatch<any, any, any>>()

  const {
    showFurnitureRemovalDrawing,
    furnitureMaskLines,
    tempFurnitureMaskLines,
    furnitureBrushHead,
    furnitureBrushSize,
    furnitureMaskHistoryStack,
    furnitureMaskHistoryIndex,
    furnitureRemovalOriginalImageUrl,
    furnitureMaskIsReEdit,
  } = useSelector((state: any) => state.itemRemoval)

  // 前回のポップアップ表示状態を追跡
  const prevShowRef = useRef(false)

  // ポップアップが開いた時の初期化処理
  useEffect(() => {
    // falseからtrueになった時のみ初期化
    if (showFurnitureRemovalDrawing && !prevShowRef.current) {
      // 再編集フラグがtrueの場合はマスクをクリアしない
      if (furnitureMaskIsReEdit) {
        // 再編集フラグをリセット
        dispatch(itemRemovalActions.setFurnitureMaskIsReEdit(false))
      } else {
        // 描画状態をリセット
        dispatch(itemRemovalActions.clearFurnitureMaskLines())
      }
    }
    prevShowRef.current = showFurnitureRemovalDrawing
  }, [showFurnitureRemovalDrawing, furnitureMaskIsReEdit, dispatch])

  const closePopup = () => {
    dispatch(itemRemovalActions.setShowFurnitureRemovalDrawing(false))
    // 描画状態をクリア
    dispatch(itemRemovalActions.clearFurnitureMaskLines())
  }

  const handleConfirm = () => {
    // プレビューを生成してポップアップを閉じる
    dispatch(itemRemovalActions.generateFurnitureMaskPreview())
  }

  const handleUndo = () => {
    dispatch(itemRemovalActions.undoFurnitureMask())
  }

  const handleRedo = () => {
    dispatch(itemRemovalActions.redoFurnitureMask())
  }

  const handleClearAll = () => {
    dispatch(itemRemovalActions.clearFurnitureMaskLines())
  }

  const canUndo = furnitureMaskHistoryIndex > 0
  const canRedo = furnitureMaskHistoryIndex < furnitureMaskHistoryStack.length - 1
  const hasLines = furnitureMaskLines.length > 0

  const setBrushHead = (shape: BrushShape) => {
    dispatch(itemRemovalActions.setFurnitureBrushHead(shape))
  }

  const renderBrushShape = (size: number) => {
    switch (furnitureBrushHead) {
      case 'circle':
        return <CircleOutlineIcon style={{ stroke: "#595959", strokeWidth: "2px", width: `${size}px`, height: `${size}px` }} />
      case 'triangle':
        return <TriangleOutlineIcon style={{ stroke: "#595959", strokeWidth: '2px', width: `${size}px`, height: `${size}px` }} />
      case 'square':
        return <SquareOutlineIcon style={{ stroke: "#595959", strokeWidth: '2px', width: `${size}px`, height: `${size}px` }} />
      default:
        return null
    }
  }

  return (
    <>
      <Modal show={showFurnitureRemovalDrawing} onClose={closePopup} size="xl">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true"></div>
        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex items-end max-h-full justify-center p-4 text-center sm:items-center sm:p-0">
            <div className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-xl">
              {/* 1行目：ヘッダー（タイトル + キャンセル/実行ボタン） */}
              <div className="py-2 px-4 bg-orange-500 text-white flex items-start justify-between">
                <div>
                  <p>• 消したい家具を塗りつぶしてください。</p>
                  <p>• 塗りつぶした部分のみがAI処理で消去されます。</p>
                </div>
                <button
                  onClick={closePopup}
                  className="px-6 py-2 ml-4 text-white hover:bg-orange-600 rounded transition-colors font-bold flex-shrink-0"
                  style={{ fontSize: '32px', lineHeight: '1' }}
                  title="閉じる"
                >
                  ×
                </button>
              </div>

              <div className="p-4">
                <div className="flex flex-col gap-2 mb-2">
                  {/* 2行目：ツールバー */}
                  <div className="flex justify-between items-center">
                    {/* 左側：描画ツール */}
                    <div className="flex items-center gap-2">
                      {/* ブラシ形状選択 */}
                      <div className="flex items-center py-1 px-2 border-r border-[#b9b7b8]">
                        <div
                          onClick={() => setBrushHead("circle")}
                          className="p-1 border-r border-[#f1f1f1] hover:cursor-pointer"
                        >
                          <CircleOutlineIcon
                            style={{
                              stroke: furnitureBrushHead === "circle" ? "#595959" : "#d7d7d7",
                              strokeWidth: "3px",
                              width: "24px",
                              height: "24px",
                            }}
                          />
                        </div>
                        <div
                          onClick={() => setBrushHead("triangle")}
                          className="p-1 border-r border-[#f1f1f1] hover:cursor-pointer"
                        >
                          <TriangleOutlineIcon
                            style={{
                              stroke: furnitureBrushHead === "triangle" ? "#595959" : "#d7d7d7",
                              strokeWidth: "3px",
                              width: "24px",
                              height: "24px",
                            }}
                          />
                        </div>
                        <div
                          onClick={() => setBrushHead("square")}
                          className="p-1 hover:cursor-pointer"
                        >
                          <SquareOutlineIcon
                            style={{
                              stroke: furnitureBrushHead === "square" ? "#595959" : "#d7d7d7",
                              strokeWidth: "3px",
                              width: "24px",
                              height: "24px",
                            }}
                          />
                        </div>
                      </div>

                      {/* サイズスライダー */}
                      <div className="flex items-center py-1 px-2 border-r border-[#b9b7b8] gap-2">
                        <div>
                          {renderBrushShape(16)}
                        </div>
                        <div>
                          <Slider
                            value={furnitureBrushSize}
                            onChange={(e) => {
                              dispatch(itemRemovalActions.setFurnitureBrushSize(e.value as number))
                            }}
                            min={10}
                            max={100}
                          />
                        </div>
                        <div>
                          {renderBrushShape(32)}
                        </div>
                      </div>

                      {/* Undo/Redo/クリア */}
                      <div className="flex items-center py-1 px-2 gap-2">
                        <button
                          onClick={handleUndo}
                          disabled={!canUndo}
                          className="p-1 hover:cursor-pointer disabled:opacity-50"
                          title="1つ前に戻る"
                        >
                          <UndoIcon style={{
                            fill: canUndo ? '#595959' : '#d1d1d1',
                            width: '24px',
                            height: '24px'
                          }} />
                        </button>
                        <button
                          onClick={handleRedo}
                          disabled={!canRedo}
                          className="p-1 hover:cursor-pointer disabled:opacity-50"
                          title="1つ先に進める"
                        >
                          <RedoIcon style={{
                            fill: canRedo ? '#595959' : '#d1d1d1',
                            width: '24px',
                            height: '24px'
                          }} />
                        </button>
                        <div className="w-[1px] h-[24px] bg-[#d7d7d7] mx-2"></div>
                        <button
                          onClick={handleClearAll}
                          disabled={!hasLines}
                          className="px-3 py-1 font-medium disabled:text-[#d1d1d1] text-sm"
                          title="すべてクリア"
                        >
                          クリア
                        </button>
                      </div>
                    </div>

                    {/* 右側：確定ボタン */}
                    <div className="flex items-center">
                      <button
                        onClick={handleConfirm}
                        disabled={!hasLines}
                        className={`px-4 py-1 font-medium rounded transition-all ${
                          hasLines
                            ? 'bg-orange-500 text-white hover:bg-orange-600'
                            : 'bg-gray-200 text-gray-400 cursor-not-allowed opacity-60'
                        }`}
                        title={hasLines ? '選択範囲を確定' : '消去する範囲を塗りつぶしてください'}
                      >
                        確定
                      </button>
                    </div>
                  </div>
                </div>

                {/* 描画エリア */}
                <FurnitureRemovalDrawing />
              </div>
            </div>
          </div>
        </div>
      </Modal>
    </>
  )
}

export default FurnitureRemovalDrawingPopup
